import { Injectable } from "@angular/core";
import { createStore, withProps, Store } from "@ngneat/elf";
import localForage from "localforage";
import { persistState } from '@ngneat/elf-persist-state';
import { SessionStorageService } from "./session-store.service";
import { Observable } from "rxjs";

@Injectable({
  providedIn: 'root'
})
export class StoreService {

  /** Use for things that need stored per browser tab. */
  private _session: SessionStorageService;
  private _stores: Record<string, Store<any>> = {};
  private _preloadedState: Record<string, any> = {};
  private _tabSessionId: string | null = null;

  constructor() {
    this._session = new SessionStorageService();
    localForage.config({
      driver: localForage.INDEXEDDB,
      name: 'app-db',
      version: 1.0,
      storeName: 'appState'
    });
    // Preload all necessary data from IndexedDB
    this.preloadState();
  }

  private async preloadState(): Promise<void> {
    const keys = await localForage.keys();
    for (const key of keys) {
      this._preloadedState[key] = await localForage.getItem(key);
    }
  }

  createStore<T>(storeKey: string, initialState: T, ...features: any[]): Store<any> {
    if (!this._stores[storeKey]) {
      const persistedState = this._preloadedState[storeKey];
      const state = persistedState ? { ...initialState, ...persistedState } : initialState;
      const store = createStore(
        { name: storeKey },
        withProps<T>(state),
        ...features
      );
      this.persistItem(storeKey, store);
      this._stores[storeKey] = store;
    }
    return this._stores[storeKey];
  }

  // Get store by key
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getStore<T>(storeKey: string): Store<any> {
    return this._stores[storeKey] as Store<any>;
  }

  // Update store by key
  updateStore<T>(storeKey: string, newState: Partial<T>) {
    const store = this.getStore<T>(storeKey);
    store.update(state => ({
      ...state,
      ...newState
    }));
  }

  async get<T>(key: string): Promise<T | null> {
    try {
      return await localForage.getItem<T>(key);
    } catch (error) {
      console.error(`Error getting key "${key}" from localForage (IndexedDB):`, error);
      return null;
    }
  }

  async set<T>(key: string, value: T): Promise<boolean> {
    try {
      await localForage.setItem(key, value);
      return true;
    } catch (error) {
      console.error(`Error setting key "${key}" in localForage (IndexedDB):`, error);
      return false;
    }
  }

  async setMany(entries: Record<string, unknown>): Promise<boolean> {
    try {
      await Promise.all(
        Object.entries(entries).map(([key, value]) => localForage.setItem(key, value))
      );
      return true;
    } catch (error) {
      console.error('Error setting multiple keys in localForage (IndexedDB):', error);
      return false;
    }
  }

  async remove(key: string): Promise<boolean> {
    try {
      await localForage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`Error removing key "${key}" from localForage (IndexedDB):`, error);
      return false;
    }
  }

  /**
 * Retrieve a value from session storage by key.
 * 
 * @param key - The session storage key.
 * @returns The stored value if present, otherwise null.
 */
  getSession<T>(key: string): T | null {
    const namespacedKey = this.withTabKey(key);
    return this._session.get<T>(namespacedKey);
  }

  /**
   * Store a value in session storage under the given key.
   * 
   * @param key - The session storage key.
   * @param value - The value to store.
   */
  setSession<T>(key: string, value: T): void {
    const namespacedKey = this.withTabKey(key);
    this._session.set<T>(namespacedKey, value);
  }

  /**
  * Remove a value in session storage under the given key.
  * 
  * @param key - The session storage key.
  */
  removeSession(key: string): void {
    const namespacedKey = this.withTabKey(key);
    this._session.remove(namespacedKey);
  }

  /**
     * Watch for changes to a specific session key.
     * Emits the current and future values as an observable stream.
     * 
     * @param key - The session storage key to observe.
     * @returns An observable that emits the value when it changes.
     */
  watchSession<T>(key: string): Observable<T | null> {
    const namespacedKey = this.withTabKey(key);
    return this._session.watch<T>(namespacedKey);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private persistItem<T>(storeKey: string, store: Store<any>): void {
    persistState(store, {
      key: storeKey,
      storage: localForage
    });
  }
  
  /** Gets a stable session ID for this browser tab */
  private getTabSessionId(): string {
    if (!this._tabSessionId) {
      if (!window.name || !window.name.startsWith("tab-session-")) {
        window.name = `tab-session-${crypto.randomUUID()}`;
      }
      this._tabSessionId = window.name;
    }
    return this._tabSessionId;
  }

  private withTabKey(key: string): string {
    return `${this.getTabSessionId()}::${key}`;
  }

}

export class StorageKeys {
  static DARK_MODE = "dark_mode";
  static ROLES = "roles";
  static UNITS = "units";
  static USER = "user";
  static RECENT_DESIGNS = "recent_designs_v2";
}

export class SessionStorageKeys {
  static SESSION_ID = "sessionId";
  static APP_IDS = "appIds";
  static DESIGN = "design";
}
